import React, { useMemo, useState } from 'react';

import classNames from 'classnames';

import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import SearchInput from 'components/search_sidebar/sidebar_search';
import { pluralize } from 'helpers/formatters/string_formats';

import '../styles.scss';
import type { CandidateDocument } from './types';

export const PickerMode = { Add: 'add', Remove: 'remove' } as const;

export type PickerMode = (typeof PickerMode)[keyof typeof PickerMode];

interface Props {
  // Upload starts with nothing chosen and that is a valid outcome: the page
  // lands in the library unattached.
  allowEmpty?: boolean;
  // Rendered above the checklist: upload puts its file summary and name fields
  // here so the checklist, search and orientation rules stay in one component.
  children?: React.ReactNode;
  documents: CandidateDocument[];
  mode: PickerMode;
  misfitPages?: (doc: CandidateDocument) => string[];
  pageOrientation: string;
  pageNames?: string[];
  saving: boolean;
  show: boolean;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (documentIds: number[]) => void;
}

interface Reason {
  label: string;
  detail?: string;
}

export const orientationMismatch = (doc: Pick<CandidateDocument, 'orientation'>, pageOrientation: string) => {
  if (!doc.orientation) return false;
  if (pageOrientation === 'unknown' || pageOrientation === 'mixed') return false;

  return doc.orientation !== pageOrientation;
};

const orientationLabel = (doc: CandidateDocument) => {
  const orientation = doc.orientation ?? '';

  return `${orientation.charAt(0).toUpperCase()}${orientation.slice(1)} document`;
};

const subjectFor = (pageNames: string[]) => {
  if (pageNames.length === 1) return `"${pageNames[0]}"`;
  if (pageNames.length > 1) return pluralize(pageNames.length, 'page');

  return null;
};

const titleFor = (mode: PickerMode, pageNames: string[]) => {
  const subject = subjectFor(pageNames);

  if (mode === PickerMode.Remove) return subject ? `Remove ${subject} from documents` : 'Remove from documents';

  return subject ? `Add ${subject} to documents` : 'Add to documents';
};

const helpTextFor = (mode: PickerMode, pageCount: number) => {
  const isMoreThanOnePage = pageCount > 1;

  if (mode === PickerMode.Remove) {
    return `Pick the documents to remove ${isMoreThanOnePage ? 'these pages' : 'this page'} from.`;
  }

  return isMoreThanOnePage
    ? 'Pick every document that should include these. The pages are added to the end of each one.'
    : 'Pick every document that should include this. The page is added to the end of each one.';
};

const selectionSummary = (count: number) => {
  if (count === 0) return 'No documents selected';

  return `${pluralize(count, 'document')} selected`;
};

export default function DocumentPicker({
  allowEmpty = false, children, documents, misfitPages, mode, pageNames = [], pageOrientation, saving, show,
  submitLabel, onClose, onSubmit,
}: Props) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const candidates = useMemo(
    () => documents.filter(doc => (mode === 'add' ? !doc.in_document : doc.in_document)),
    [documents, mode]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? candidates.filter(doc => doc.name.toLowerCase().includes(term)) : candidates;
  }, [candidates, search]);

  const groups = useMemo(() => {
    const byType = new Map<string, CandidateDocument[]>();
    visible.forEach((doc) => {
      const key = doc.document_type || 'Other';
      byType.set(key, [...(byType.get(key) || []), doc]);
    });
    return [...byType.entries()];
  }, [visible]);

  const reasonFor = (doc: CandidateDocument): Reason | null => {
    if (mode === PickerMode.Remove) return null;

    if (misfitPages) {
      const misfits = [...new Set(misfitPages(doc))];
      if (misfits.length === 0) return null;

      return { label: orientationLabel(doc), detail: `Doesn't fit ${misfits.join(', ')}` };
    }

    if (orientationMismatch(doc, pageOrientation)) return { label: orientationLabel(doc) };

    return null;
  };

  const selectable = visible.filter(doc => reasonFor(doc) === null);

  const toggle = (id: number, checked: boolean) => {
    setSelectedIds(current => (checked ? [...current, id] : current.filter(i => i !== id)));
  };

  const selectAllVisible = () => {
    setSelectedIds(current => [...new Set([...current, ...selectable.map(doc => doc.id)])]);
  };

  const close = () => {
    if (saving) return;

    setSelectedIds([]);
    setSearch('');
    onClose();
  };

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered scrollable>
        <Modal.Header>
          <Modal.Title>{titleFor(mode, pageNames)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">{helpTextFor(mode, pageNames.length)}</p>
          {children}
          <div className="d-flex align-items-center gap-3 mb-3">
            <SearchInput
              placeholder="Search documents"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
            />
            <Button size="sm" variant="ghost" onClick={selectAllVisible}>
              Select all
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
              Clear
            </Button>
          </div>
          {groups.length > 0 && (
            <List className="document-picker-list">
              {groups.map(([type, docs]) => (
                <React.Fragment key={type}>
                  <List.Item className="bg-light text-uppercase fs-small fw-semibold text-muted">{type}</List.Item>
                  {docs.map((doc) => {
                    const reason = reasonFor(doc);
                    return (
                      <List.Item
                        className={classNames(
                          'document-picker-row d-flex align-items-center justify-content-between gap-3',
                          { 'is-unavailable': reason }
                        )}
                        key={doc.id}
                      >
                        <Checkbox
                          checked={selectedIds.includes(doc.id)}
                          className="document-picker-name"
                          disabled={reason !== null}
                          label={doc.name}
                          onCheckedChange={(checked: boolean | 'indeterminate') => toggle(doc.id, checked === true)}
                        />
                        {reason && (
                          <span className="fs-small text-nowrap" title={reason.detail}>{reason.label}</span>
                        )}
                      </List.Item>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>
          )}
          {visible.length === 0 && <p className="text-muted mb-0">No documents match.</p>}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted me-auto">{selectionSummary(selectedIds.length)}</span>
          <Button disabled={saving} variant="ghost" onClick={close}>Cancel</Button>
          <Button
            disabled={saving || (selectedIds.length === 0 && !allowEmpty)}
            variant="primary"
            onClick={() => onSubmit(selectedIds)}
          >
            {submitLabel || (mode === PickerMode.Add ? 'Add to documents' : 'Remove from documents')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
